/**
 * Hydration: what a `GET /auth/me` outcome means for the session.
 *
 * `/auth/me` is the single hydration call every session path converges on — fresh sign-in and
 * cold-start restore alike (authentication ADR §2, §3). It is also the ONLY place the trainer's
 * role is knowable: the backend maps the token's `sub` onto an identity and then looks the roles
 * up in local Postgres per request, so a token that is perfectly valid at Supabase can belong to
 * an account with any role, or none.
 *
 * ## The role gate is asserted positively, and that is not a stylistic choice
 *
 * `AuthProcessor.ResolveFrontendRole` reads:
 *
 *     if (roles.Contains(PersonalTrainer)) return PersonalTrainer;
 *     if (roles.Contains(Admin))          return Admin;
 *     return Customer;
 *
 * `Customer` is the **fallback**, not a check. A profile row with no role assignments at all
 * reports `Customer`. So "not Customer" can never be read as "trainer", and the only safe test is
 * `role === 'PersonalTrainer'`. Everything else — `Admin`, `Customer`, an unrecognised string, a
 * missing field — is a refusal.
 *
 * ## Routing an error keys off `code`, never off the status
 *
 * `train-with-me/Domain/AuthErrorCodes.cs` says so explicitly: `profile_missing` arrives as 404
 * from `GET /auth/me` but as 403 from a role-gated endpoint. Anything this module cannot classify
 * is inconclusive, and an inconclusive hydration NEVER signs anybody out — at boot it degrades,
 * mid-session it degrades. That guarantee lives in `sessionMachine.ts`; this module's job is only
 * to never hand it an event that would break it.
 *
 * Pure: type-only imports plus one `instanceof`. No React, no DOM, no i18n, no fetch.
 */

import type { TrainWithMeModulesAccountsDtosResponsesGetMeResponse as GetMeResponse } from '~/shared/api/generated/model';
import { ApiError } from '~/shared/api/mutator';

import type { SessionEvent, SessionUser, UserRole } from './sessionMachine';

/** The one role this app admits. */
const TRAINER_ROLE = 'PersonalTrainer' satisfies UserRole;

/** `AuthErrorCodes.ProfileMissing` — a valid token with no local profile row. */
const PROFILE_MISSING_CODE = 'profile_missing';

const KNOWN_ROLES: readonly UserRole[] = ['PersonalTrainer', 'Admin', 'Customer'];

/**
 * Narrow the backend's role string. Returns `null` for anything unrecognised rather than
 * defaulting to a role — a default here would be a silent authorisation decision.
 */
function parseRole(role: string | null | undefined): UserRole | null {
  return KNOWN_ROLES.find((known) => known === role) ?? null;
}

/**
 * Flatten `/auth/me` into the session's own view of the trainer.
 *
 * The generated type DOES mark id, email and role required — they are non-optional keys — and
 * every one of them is still `string | null`, because the backend's C# nullable annotations reach
 * the OpenAPI document as `nullable` even where the property is always sent. That `| null` is
 * what the `?? ''` below answers, and this is the seam where it is answered once. It runs only
 * after the role gate has passed, so `role` is known to be `PersonalTrainer`.
 */
function toSessionUser(me: GetMeResponse): SessionUser {
  const firstName = me.firstName ?? '';
  const lastName = me.lastName ?? '';
  const email = me.email ?? '';

  return {
    id: me.id ?? '',
    email,
    firstName,
    lastName,
    displayName: [firstName, lastName].filter(Boolean).join(' ') || email,
    role: TRAINER_ROLE,
    avatarUrl: me.avatarUrl ?? null,
    trainerId: me.personalTrainer?.id ?? null,
    nutritionEnabled: me.personalTrainer?.nutritionEnabled ?? false,
    selfCustomerId: typeof me.customer?.id === 'number' ? me.customer.id : null,
  };
}

/** True when the backend named the profile row as the thing that is missing. */
function isProfileMissing(error: unknown): boolean {
  return error instanceof ApiError && error.code === PROFILE_MISSING_CODE;
}

/** What happened when hydration was attempted. */
export type HydrationResult =
  | { readonly ok: true; readonly me: GetMeResponse }
  | { readonly ok: false; readonly error: unknown };

/**
 * When hydration ran. It changes only which inconclusive event is produced: a cold start has no
 * user to keep rendering, a live session does.
 */
export type HydrationPhase = 'boot' | 'live';

/**
 * The single decision this module exposes: one hydration outcome in, one session event out.
 *
 * Deliberately not four exported predicates the caller combines — that combination *is* the
 * decision, and putting it here is what lets a test reach it without a Supabase client, a fetch
 * or a renderer.
 */
export function sessionEventForHydration(
  result: HydrationResult,
  phase: HydrationPhase,
): SessionEvent {
  if (result.ok) {
    const role = parseRole(result.me.role);
    return role === TRAINER_ROLE
      ? { type: 'HYDRATED', user: toSessionUser(result.me) }
      : { type: 'ROLE_REFUSED', role };
  }

  if (isProfileMissing(result.error)) return { type: 'PROFILE_MISSING' };

  return phase === 'boot'
    ? { type: 'BOOT_HYDRATION_INCONCLUSIVE' }
    : { type: 'AUTH_TRANSIENT_FAILURE' };
}
